import { useEffect, useRef } from 'react';
import { matchDwellPage, type DwellPageMatch } from './dwellPages';

export interface DwellDetectorOptions {
  readonly enabled: boolean;
  readonly pathname: string;
  readonly thresholdMs?: number;
  readonly cooldownMs?: number;
  // Resolve `false` (or reject) to release the cooldown for retry; `true`/void keeps it.
  // `signal` aborts on teardown (route change / unmount) so an in-flight dwell can bail.
  readonly onDwell: (
    match: DwellPageMatch,
    signal: AbortSignal
  ) => void | Promise<boolean>;
}

const DEFAULT_THRESHOLD_MS = 60000;
const DEFAULT_COOLDOWN_MS = 600000;
// Only *edits* reset the idle clock. Reading, scrolling, mouse-moving and
// clicking are passive — a user doing those may be stuck working through the
// page, which is exactly who we want to offer help to, so they must NOT keep
// pushing the nudge away. `input`/`change` bubble to window from any form
// control (typing, adjusting IRPM, toggling a select), so one listener covers
// "the user made a change".
const EDIT_EVENTS: readonly string[] = ['input', 'change'];

export function useDwellDetector(options: DwellDetectorOptions): void {
  const { enabled, pathname, onDwell } = options;
  const thresholdMs = options.thresholdMs ?? DEFAULT_THRESHOLD_MS;
  const cooldownMs = options.cooldownMs ?? DEFAULT_COOLDOWN_MS;

  const onDwellRef = useRef(onDwell);
  onDwellRef.current = onDwell;

  const lastFiredRef = useRef<Record<string, number>>({});

  // Read the latest pathname inside the effect without making it a dependency.
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;

  // All tabs of a detail page share one dwell identity (matchDwellPage ignores
  // the tab segment), so key the effect on pageKey:entityId — NOT the raw
  // pathname. Otherwise switching tabs on the same entity tears the effect
  // down, aborting the in-flight signal/poll and burning the cooldown.
  const match = matchDwellPage(pathname);
  const dwellKey = match ? `${match.pageKey}:${match.entityId ?? ''}` : '';

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (!enabled || !dwellKey) return undefined;

    const activeMatch = matchDwellPage(pathnameRef.current);
    if (!activeMatch) return undefined;

    let timer: ReturnType<typeof setTimeout> | undefined;
    // Aborted on cleanup so an in-flight dwell (signal POST + ~60s poll) can
    // bail instead of nudging a page the user has already navigated away from.
    const controller = new AbortController();

    const fire = () => {
      const key = `${activeMatch.pageKey}:${activeMatch.entityId ?? ''}`;
      const now = Date.now();
      const last = lastFiredRef.current[key] ?? 0;
      if (now - last < cooldownMs) return;
      lastFiredRef.current[key] = now; // optimistic; released below if the dwell fails
      Promise.resolve(onDwellRef.current(activeMatch, controller.signal))
        .catch(() => false)
        .then((kept) => {
          if (kept === false) lastFiredRef.current[key] = last;
        });
    };

    const arm = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(fire, thresholdMs);
    };

    const onEdit = () => arm();

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        if (timer) clearTimeout(timer);
      } else {
        arm();
      }
    };

    EDIT_EVENTS.forEach((evt) =>
      window.addEventListener(evt, onEdit, { passive: true })
    );
    document.addEventListener('visibilitychange', onVisibility);
    arm();

    return () => {
      controller.abort();
      if (timer) clearTimeout(timer);
      EDIT_EVENTS.forEach((evt) => window.removeEventListener(evt, onEdit));
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled, dwellKey, thresholdMs, cooldownMs]);
}
